import { useEffect, useRef, useState } from "react";
import { apiGet } from "../../api";

/**
 * Map the Employees shell's filter state onto analytics query parameters.
 *
 * `selectedEmployee` holds a USER id, because TimeEntry.created_by is a User
 * FK -- the employee dropdown must be populated from `user_id`, not the
 * Employee `id`. Passing an Employee pk here returns a plausible wrong
 * answer rather than an error, which is the worst kind.
 *
 * EVERY filter the shell offers has to be forwarded. `selectedTimeCode` and
 * `selectedYearEnd` used to be dropped here while still narrowing the summary
 * cards and the three client-side tabs, so setting Year End moved the TOTAL
 * HOURS card and left the Time Codes table below it alone: two disagreeing
 * totals on one screen, in an accounting app.
 */
export function toQuery(filters = {}) {
  const q = {};
  if (filters.startDate) q.start_date = filters.startDate;
  if (filters.endDate) q.end_date = filters.endDate;
  if (filters.selectedEmployee) q.employee = filters.selectedEmployee;
  if (filters.selectedClient) q.client = filters.selectedClient;
  if (filters.selectedBillable) q.billable = filters.selectedBillable;
  if (filters.selectedTimeCode) q.time_code = filters.selectedTimeCode;
  if (filters.selectedYearEnd) q.year_end = filters.selectedYearEnd;
  return q;
}

/**
 * GET /api/time-analytics/<path> with `params`, refetching when either changes.
 *
 * `params` is compared by its serialised form rather than by identity, so a
 * caller passing a fresh object literal every render does not cause an
 * infinite refetch loop.
 */
export function useAnalytics(path, params = {}) {
  const [state, setState] = useState({ data: null, loading: true, error: "" });
  const query = new URLSearchParams(params).toString();
  // The initial state above is already {loading: true, error: ""}, so the
  // very first effect run has nothing to set -- doing it anyway is a
  // redundant synchronous update that only exists to reset state on a
  // later refetch (path or query changing after the first load).
  const mounted = useRef(false);

  useEffect(() => {
    let cancelled = false;
    if (mounted.current) {
      setState((s) => ({ ...s, loading: true, error: "" }));
    }
    mounted.current = true;

    // A null path means "not now" -- TrendStrip does this at day
    // granularity, where there is nothing to chart. Hooks cannot be
    // called conditionally, so the caller suppresses the fetch instead
    // of skipping the hook.
    if (!path) {
      setState({ data: null, loading: false, error: "" });
      return () => { cancelled = true; };
    }

    const url = `/time-analytics/${path}${query ? `?${query}` : ""}`;
    apiGet(url)
      .then((data) => {
        if (!cancelled) setState({ data, loading: false, error: "" });
      })
      .catch((e) => {
        if (!cancelled) {
          setState({
            data: null, loading: false,
            error: `Could not load ${path}. ${e.message || ""}`.trim(),
          });
        }
      });

    return () => { cancelled = true; };
  }, [path, query]);

  return state;
}
